import mapboxGl from 'mapbox-gl';
import { cyan, teal } from '@carbon/colors';
import { Geometry } from 'geojson';

export const POLYGON_SELECTED_COLOR = teal[30];
export const POLYGON_UNSELECTED_COLOR = cyan[90];
export const BACKGROUND_COLOR = teal[40];

function findCenter(geometry: Geometry): {
  longitude: number;
  latitude: number;
};
function findCenter(geometries: Geometry[]): {
  longitude: number;
  latitude: number;
};
function findCenter(geometryOrGeometries: Geometry | Geometry[]): {
  longitude: number;
  latitude: number;
} {
  const bounds = new mapboxGl.LngLatBounds();
  const geometries = Array.isArray(geometryOrGeometries)
    ? geometryOrGeometries
    : [geometryOrGeometries];

  // Filter out null/undefined geometries
  const validGeometries = geometries.filter(
    (g) => g !== null && g !== undefined
  );

  // Return default center if no valid geometries
  if (validGeometries.length === 0) {
    return { longitude: -98.5795, latitude: 39.8283 }; // US center
  }

  validGeometries.forEach((geometry) => {
    let coordinates: GeoJSON.Position[] = [];
    if (geometry.type === 'Polygon') {
      coordinates = geometry.coordinates.flat(1);
    } else if (geometry.type === 'MultiPolygon') {
      coordinates = geometry.coordinates.flat(2);
    }
    coordinates.forEach((coordinate) => {
      bounds.extend([coordinate[0], coordinate[1]]);
    });
  });

  const center = bounds.getCenter();

  // If getCenter() returns undefined (empty bounds), return default
  if (!center) {
    return { longitude: -98.5795, latitude: 39.8283 }; // US center
  }

  return { longitude: center.lng, latitude: center.lat };
}

function extractCoordinatesFromGeometry(
  geometry: GeoJSON.Geometry
): number[][] {
  if (!geometry) {
    return [];
  }
  if (geometry.type === 'Polygon') {
    return geometry.coordinates.flat(1);
  } else if (geometry.type === 'MultiPolygon') {
    return geometry.coordinates.flat(2);
  }
  return [];
}

function calculateBounds(coordinates: number[][]): mapboxGl.LngLatBounds {
  return coordinates.reduce(
    (bounds, coordinate) => bounds.extend([coordinate[0], coordinate[1]]),
    new mapboxGl.LngLatBounds()
  );
}

function buildPolygonStyle({
  color,
  lineColor,
  fillOpacity = 0.3,
  lineOpacity = 0.4,
  lineWidth = 3,
}: {
  color: string;
  lineColor?: string;
  fillOpacity?: number;
  lineOpacity?: number;
  lineWidth?: number;
}) {
  return Object.seal({
    fill: {
      'fill-color': color,
      'fill-opacity': fillOpacity,
    },
    fillHighlighted: {
      'fill-color': color,
      'fill-opacity': 1,
    },
    lineHighlighted: {
      'line-color': 'yellow',
      'line-width': 3,
      'line-opacity': 1,
    },
    line: {
      'line-color': lineColor || color,
      'line-width': lineWidth,
      'line-opacity': lineOpacity,
    },
  });
}

function findBounds(geometry: Geometry): [[number, number], [number, number]];
function findBounds(
  geometries: Geometry[]
): [[number, number], [number, number]];
function findBounds(
  geometryOrGeometries: Geometry | Geometry[]
): [[number, number], [number, number]] {
  const bounds = new mapboxGl.LngLatBounds();
  const geometries = Array.isArray(geometryOrGeometries)
    ? geometryOrGeometries
    : [geometryOrGeometries];

  // Filter out null/undefined geometries
  const validGeometries = geometries.filter(
    (g) => g !== null && g !== undefined
  );

  // Return default US bounds if no valid geometries
  if (validGeometries.length === 0) {
    return [
      [-125, 25], // Southwest corner of US
      [-65, 50], // Northeast corner of US
    ];
  }

  validGeometries.forEach((geometry) => {
    let coordinates: GeoJSON.Position[] = [];
    if (geometry.type === 'Polygon') {
      coordinates = geometry.coordinates.flat(1);
    } else if (geometry.type === 'MultiPolygon') {
      coordinates = geometry.coordinates.flat(2);
    }
    coordinates.forEach((coordinate) => {
      bounds.extend([coordinate[0], coordinate[1]]);
    });
  });

  const sw = bounds.getSouthWest();
  const ne = bounds.getNorthEast();

  // Check if bounds are valid
  if (!sw || !ne) {
    return [
      [-125, 25], // Southwest corner of US
      [-65, 50], // Northeast corner of US
    ];
  }

  return [
    [sw.lng, sw.lat],
    [ne.lng, ne.lat],
  ];
}

/** MapBox fill + line paint styles produced by {@link buildPolygonStyle}. */
export type PolygonStyle = ReturnType<typeof buildPolygonStyle>;

export {
  findCenter,
  findBounds,
  buildPolygonStyle,
  extractCoordinatesFromGeometry,
  calculateBounds,
};
